import React, { useState, useRef } from 'react';
import { Settings, Save, Globe, Bell, Shield, Database, Palette, Mail, Loader2, RefreshCw, AlertCircle, Clock, Upload, Eye, EyeOff, Trash2, RotateCcw, Move, X, Image as ImageIcon, Plus, MessageSquare, Key, Smartphone, Play, CheckSquare, Square, Copy, Check, Sparkles, Server, Lock } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAppSettings, AppSettings } from '../../shared/hooks/hooks/useAppSettings';
import {
  siteSettingsSchema,
  contactSettingsSchema,
  businessHoursSettingsSchema,
  carouselSettingsSchema,
  systemSettingsSchema,
  wahaSettingsSchema,
  emailServerSettingsSchema,
  SiteSettings,
  ContactSettings,
  BusinessHoursSettings,
  CarouselSettings,
  SystemSettings,
  WahaSettings,
  EmailServerSettings
} from '../../shared/types/schemas/validationSchemas';
import { useSupabaseImages } from '../../shared/hooks/hooks/useSupabaseImages';
import { CarouselImage } from '../../shared/services/lib/supabase';
import { PhoneInput } from '../ui/PhoneInput';
import { WahaTestModal } from '../shared/WahaTestModal';
import { EmailServerTestModal } from '../shared/EmailServerTestModal';

type SettingsCategory = 'site' | 'contact' | 'email' | 'waha' | 'business_hours' | 'carousel' | 'system';

const AdminSettings: React.FC = () => {
  const { settings, loading, saving, error, updateSetting, updateMultipleSettings, loadSettings } = useAppSettings();
  const [activeCategory, setActiveCategory] = useState<SettingsCategory>('site');
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [isEmailTestModalOpen, setIsEmailTestModalOpen] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showEmailPassword, setShowEmailPassword] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    images,
    loading: imagesLoading,
    error: imagesError,
    loadImages,
    addImage,
    updateImage,
    toggleImage,
    moveToTrash,
    restoreImage,
    permanentlyDelete,
    reorderImages,
    getActiveImages,
    getDeletedImages
  } = useSupabaseImages();

  // Função para obter o schema correto baseado na categoria
  const getSchemaForCategory = (category: SettingsCategory) => {
    switch (category) {
      case 'site':
        return siteSettingsSchema;
      case 'contact':
        return contactSettingsSchema;
      case 'email':
        return emailServerSettingsSchema;
      case 'waha':
        return wahaSettingsSchema;
      case 'business_hours':
        return businessHoursSettingsSchema;
      case 'carousel':
        return carouselSettingsSchema;
      case 'system':
        return systemSettingsSchema;
      default:
        return siteSettingsSchema;
    }
  };

  // Função para obter valores padrão baseado na categoria
  const getDefaultValues = (category: SettingsCategory): any => {
    if (!settings) return {};

    switch (category) {
      case 'site':
        return { site_title: settings.site_title || '' };
      case 'contact':
        return {
          contact_email: settings.contact_email || '',
          phone: settings.phone || '',
          address: settings.address || '',
          social_instagram: settings.social_instagram || '',
          social_whatsapp: settings.social_whatsapp || '',
        };
      case 'email':
        return {
          email_smtp_user: settings.email_smtp_user || '',
          email_smtp_password: settings.email_smtp_password || '',
          email_incoming_host: settings.email_incoming_host || '',
          email_imap_port: settings.email_imap_port || 993,
          email_pop3_port: settings.email_pop3_port || 995,
          email_outgoing_host: settings.email_outgoing_host || '',
          email_smtp_port: settings.email_smtp_port || 465,
          email_from_name: settings.email_from_name || '',
          email_from_address: settings.email_from_address || '',
          email_security: settings.email_security || 'ssl_tls',
          email_auth_required: settings.email_auth_required ?? true,
          email_enabled: settings.email_enabled ?? true,
          email_msg_order_created_subject: settings.email_msg_order_created_subject || 'Pedido Recebido #{numero_pedido} - {evento}',
          email_msg_order_created_body: settings.email_msg_order_created_body || '',
          email_msg_order_confirmed_subject: settings.email_msg_order_confirmed_subject || '🎉 Ingressos Confirmados! Pedido #{numero_pedido} - {evento}',
          email_msg_order_confirmed_body: settings.email_msg_order_confirmed_body || '',
          email_msg_order_cancelled_subject: settings.email_msg_order_cancelled_subject || 'Pedido Cancelado #{numero_pedido} - {evento}',
          email_msg_order_cancelled_body: settings.email_msg_order_cancelled_body || '',
        };
      case 'waha':
        return {
          waha_api_url: settings.waha_api_url || '',
          waha_session_name: settings.waha_session_name || 'default',
          waha_api_key: settings.waha_api_key || '',
          waha_enabled: settings.waha_enabled ?? true,
          waha_msg_order_created: settings.waha_msg_order_created || '',
          waha_msg_order_confirmed: settings.waha_msg_order_confirmed || '',
          waha_msg_order_cancelled: settings.waha_msg_order_cancelled || '',
        };
      case 'business_hours':
        return {
          business_hours_weekdays: settings.business_hours_weekdays || '',
          business_hours_weekend: settings.business_hours_weekend || '',
          business_hours_closed_days: settings.business_hours_closed_days || '',
        };
      case 'carousel':
        return {
          carousel_autoplay: settings.carousel_autoplay ?? true,
          carousel_interval: Math.round((settings.carousel_interval || 5000) / 1000), // Converter ms para s
          testimonial_carousel_interval: Math.round((settings.testimonial_carousel_interval || 8000) / 1000), // Converter ms para s
        };
      case 'system':
        return {
          max_file_size: settings.max_file_size || 5242880, // 5MB
          allowed_file_types: settings.allowed_file_types || ['image/jpeg', 'image/png', 'image/webp'],
          backup_retention_days: settings.backup_retention_days || 30,
          notification_email: settings.notification_email || '',
        };
      default:
        return {};
    }
  };

  // Configurar react-hook-form com schema dinâmico
  const form = useForm({
    resolver: zodResolver(getSchemaForCategory(activeCategory)),
    defaultValues: getDefaultValues(activeCategory),
    mode: 'onChange',
  });

  const { register, handleSubmit, formState: { errors, isDirty }, reset, watch, setValue } = form;

  // Resetar form quando categoria ou settings mudarem
  React.useEffect(() => {
    reset(getDefaultValues(activeCategory));
  }, [activeCategory, settings, reset]);

  // Função para salvar configurações da categoria
  const onSubmit = async (data: any) => {
    // Converter segundos para milissegundos antes de salvar (apenas para campos de carrossel)
    if (activeCategory === 'carousel') {
      const processedData = {
        ...data,
        carousel_interval: data.carousel_interval * 1000, // Converter s para ms
        testimonial_carousel_interval: data.testimonial_carousel_interval * 1000, // Converter s para ms
      };
      const success = await updateMultipleSettings(processedData);
      if (success) {
        reset(data); // Reset com valores em segundos para manter a interface
      }
    } else {
      const success = await updateMultipleSettings(data);
      if (success) {
        reset(data); // Reset para limpar isDirty
      }
    }
  };

  const getCategoryFields = (category: SettingsCategory): (keyof AppSettings)[] => {
    switch (category) {
      case 'site':
        return ['site_title'];
      case 'contact':
        return ['contact_email', 'phone', 'address', 'social_instagram', 'social_whatsapp'];
      case 'email':
        return [
          'email_smtp_user',
          'email_smtp_password',
          'email_incoming_host',
          'email_imap_port',
          'email_pop3_port',
          'email_outgoing_host',
          'email_smtp_port',
          'email_from_name',
          'email_from_address',
          'email_security',
          'email_auth_required',
          'email_enabled',
          'email_msg_order_created_subject',
          'email_msg_order_created_body',
          'email_msg_order_confirmed_subject',
          'email_msg_order_confirmed_body',
          'email_msg_order_cancelled_subject',
          'email_msg_order_cancelled_body'
        ];
      case 'waha':
        return ['waha_api_url', 'waha_session_name', 'waha_api_key', 'waha_enabled', 'waha_msg_order_created', 'waha_msg_order_confirmed', 'waha_msg_order_cancelled'];
      case 'business_hours':
        return ['business_hours_weekdays', 'business_hours_weekend', 'business_hours_closed_days'];
      case 'carousel':
        return ['carousel_autoplay', 'carousel_interval', 'testimonial_carousel_interval'];
      case 'system':
        return ['max_file_size', 'allowed_file_types', 'backup_retention_days', 'notification_email'];
      default:
        return [];
    }
  };

  const formatFileSize = (bytes: number): string => {
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const parseFileSize = (sizeStr: string): number => {
    const size = parseFloat(sizeStr);
    return size * 1024 * 1024;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-2 text-gray-600">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Carregando configurações...</span>
        </div>
      </div>
    );
  }

  if (error && !settings) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center gap-2 text-red-800 mb-2">
          <Shield className="h-5 w-5" />
          <span className="font-semibold">Erro ao carregar configurações</span>
        </div>
        <p className="text-red-700 mb-4">{error}</p>
        <button
          onClick={loadSettings}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Tentar Novamente
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Configurações do Sistema
            </h1>
            <p className="text-gray-600">
              Gerencie as configurações gerais da aplicação
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex gap-2">
            <button
              onClick={loadSettings}
              disabled={loading}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Recarregar
            </button>
            <button
              onClick={handleSubmit(onSubmit)}
              disabled={!isDirty || saving}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {saving ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {[
              { id: 'site', name: 'Site', icon: Globe },
              { id: 'contact', name: 'Contato', icon: Smartphone },
              { id: 'email', name: 'Servidor de E-mail', icon: Mail },
              { id: 'waha', name: 'WhatsApp (WAHA)', icon: MessageSquare },
              { id: 'business_hours', name: 'Horários', icon: Clock },
              { id: 'carousel', name: 'Carrossel', icon: Palette },
              { id: 'system', name: 'Sistema', icon: Database }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveCategory(tab.id as SettingsCategory)}
                  className={`${activeCategory === tab.id
                      ? 'border-purple-500 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Category Content */}
        <div className="p-6">
          {activeCategory === 'site' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Configurações do Site</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Título do Site
                </label>
                <input
                  type="text"
                  {...register('site_title')}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${errors.site_title
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-300'
                    }`}
                  placeholder="Nome do seu site"
                />
                {errors.site_title && (
                  <div className="flex items-center gap-1 mt-1 text-red-600 text-sm">
                    <AlertCircle className="h-4 w-4" />
                    <span>{String(errors.site_title?.message || '')}</span>
                  </div>
                )}
                <p className="text-sm text-gray-500 mt-1">
                  Este será o título principal exibido no site
                </p>
              </div>
            </div>
          )}

          {activeCategory === 'contact' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações de Contato</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email de Contato
                  </label>
                  <input
                    type="email"
                    {...register('contact_email')}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${errors.contact_email
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                      }`}
                    placeholder="contato@exemplo.com"
                  />
                  {errors.contact_email && (
                    <div className="flex items-center gap-1 mt-1 text-red-600 text-sm">
                      <AlertCircle className="h-4 w-4" />
                      <span>{String(errors.contact_email?.message || '')}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefone
                  </label>
                  <PhoneInput
                    value={watch('phone') || ''}
                    onChange={(value) => setValue('phone', value, { shouldDirty: true })}
                    error={!!errors.phone}
                    placeholder="(11) 99999-9999"
                  />
                  {errors.phone && (
                    <div className="flex items-center gap-1 mt-1 text-red-600 text-sm">
                      <AlertCircle className="h-4 w-4" />
                      <span>{String(errors.phone?.message || '')}</span>
                    </div>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Endereço
                  </label>
                  <input
                    type="text"
                    {...register('address')}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${errors.address
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                      }`}
                    placeholder="Cidade, Estado"
                  />
                  {errors.address && (
                    <div className="flex items-center gap-1 mt-1 text-red-600 text-sm">
                      <AlertCircle className="h-4 w-4" />
                      <span>{String(errors.address?.message || '')}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Instagram
                  </label>
                  <input
                    type="text"
                    {...register('social_instagram')}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${errors.social_instagram
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                      }`}
                    placeholder="@usuario"
                  />
                  {errors.social_instagram && (
                    <div className="flex items-center gap-1 mt-1 text-red-600 text-sm">
                      <AlertCircle className="h-4 w-4" />
                      <span>{String(errors.social_instagram?.message || '')}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    WhatsApp
                  </label>
                  <input
                    type="tel"
                    {...register('social_whatsapp')}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${errors.social_whatsapp
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                      }`}
                    placeholder="+5511999999999"
                  />
                  {errors.social_whatsapp && (
                    <div className="flex items-center gap-1 mt-1 text-red-600 text-sm">
                      <AlertCircle className="h-4 w-4" />
                      <span>{String(errors.social_whatsapp?.message || '')}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Categoria Servidor de E-mail */}
          {activeCategory === 'email' && (
            <div className="space-y-6">
              {/* Header do Card E-mail */}
              <div className="flex items-center gap-4 pb-4 border-b border-gray-100">
                <div className="w-12 h-12 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shadow-sm shrink-0">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Servidor de E-mail (IMAP / POP3 / SMTP)
                  </h3>
                  <p className="text-sm text-gray-500">
                    Configure os parâmetros do seu servidor de e-mail para envio de notificações e mensagens do sistema.
                  </p>
                </div>
              </div>

              {/* Card no formato da referência: Secure SSL/TLS Settings */}
              <div className="rounded-2xl border border-sky-300/80 shadow-sm overflow-hidden bg-white">
                {/* Header Azul do Card */}
                <div className="bg-[#2080cd] px-5 py-3.5 text-white flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-sky-100" />
                    <span className="font-semibold text-sm tracking-wide">
                      Secure SSL/TLS Settings (Recomendado)
                    </span>
                  </div>
                  <span className="text-[11px] bg-white/20 px-2.5 py-0.5 rounded-full font-medium text-white">
                    Portas Seguras SSL/TLS
                  </span>
                </div>

                {/* Conteúdo em linhas divididas */}
                <div className="divide-y divide-sky-100 text-sm">
                  {/* Linha 1: Nome de usuário */}
                  <div className="p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-4 gap-3 sm:gap-4 items-center bg-white hover:bg-sky-50/20 transition-colors">
                    <div className="sm:col-span-1">
                      <label className="font-medium text-gray-800 block">
                        Nome de usuário:
                      </label>
                    </div>
                    <div className="sm:col-span-3">
                      <input
                        type="text"
                        {...register('email_smtp_user')}
                        placeholder="betternow@cesire.com.br"
                        className={`w-full px-3.5 py-2.5 bg-slate-50/60 border rounded-xl text-slate-800 focus:bg-white focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all text-sm ${
                          errors.email_smtp_user ? 'border-red-300 bg-red-50' : 'border-slate-200'
                        }`}
                      />
                      {errors.email_smtp_user && (
                        <div className="flex items-center gap-1 mt-1 text-red-600 text-xs">
                          <AlertCircle className="h-3.5 w-3.5" />
                          <span>{String(errors.email_smtp_user?.message || '')}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Linha 2: Senha */}
                  <div className="p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-4 gap-3 sm:gap-4 items-start bg-white hover:bg-sky-50/20 transition-colors">
                    <div className="sm:col-span-1 pt-2">
                      <label className="font-medium text-gray-800 block">
                        Senha:
                      </label>
                    </div>
                    <div className="sm:col-span-3">
                      <div className="relative">
                        <input
                          type={showEmailPassword ? 'text' : 'password'}
                          {...register('email_smtp_password')}
                          placeholder="••••••••••••••••"
                          className={`w-full px-3.5 py-2.5 pr-11 bg-slate-50/60 border rounded-xl text-slate-800 focus:bg-white focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all text-sm font-mono ${
                            errors.email_smtp_password ? 'border-red-300 bg-red-50' : 'border-slate-200'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowEmailPassword(!showEmailPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1"
                        >
                          {showEmailPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {errors.email_smtp_password && (
                        <div className="flex items-center gap-1 mt-1 text-red-600 text-xs">
                          <AlertCircle className="h-3.5 w-3.5" />
                          <span>{String(errors.email_smtp_password?.message || '')}</span>
                        </div>
                      )}
                      <p className="text-xs text-gray-500 italic mt-1.5">
                        Use a senha da conta de email.
                      </p>
                    </div>
                  </div>

                  {/* Linha 3: Servidor de entrada */}
                  <div className="p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-4 gap-3 sm:gap-4 items-start bg-white hover:bg-sky-50/20 transition-colors">
                    <div className="sm:col-span-1 pt-2">
                      <label className="font-medium text-gray-800 block">
                        Servidor de entrada:
                      </label>
                    </div>
                    <div className="sm:col-span-3 space-y-3">
                      <div>
                        <input
                          type="text"
                          {...register('email_incoming_host')}
                          placeholder="mail.cesire.com.br"
                          className={`w-full px-3.5 py-2.5 bg-slate-50/60 border rounded-xl text-slate-800 focus:bg-white focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all text-sm ${
                            errors.email_incoming_host ? 'border-red-300 bg-red-50' : 'border-slate-200'
                          }`}
                        />
                        {errors.email_incoming_host && (
                          <div className="flex items-center gap-1 mt-1 text-red-600 text-xs">
                            <AlertCircle className="h-3.5 w-3.5" />
                            <span>{String(errors.email_incoming_host?.message || '')}</span>
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                        <div>
                          <label className="block text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1">
                            <span className="underline decoration-dotted font-bold text-slate-700">IMAP Port</span>: 993 (SSL)
                          </label>
                          <input
                            type="number"
                            {...register('email_imap_port', { valueAsNumber: true })}
                            placeholder="993"
                            className="w-full px-3 py-2 bg-slate-50/60 border border-slate-200 rounded-lg text-xs text-slate-800 focus:bg-white focus:ring-2 focus:ring-sky-500"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1">
                            <span className="underline decoration-dotted font-bold text-slate-700">POP3 Port</span>: 995 (SSL)
                          </label>
                          <input
                            type="number"
                            {...register('email_pop3_port', { valueAsNumber: true })}
                            placeholder="995"
                            className="w-full px-3 py-2 bg-slate-50/60 border border-slate-200 rounded-lg text-xs text-slate-800 focus:bg-white focus:ring-2 focus:ring-sky-500"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Linha 4: Servidor de saída */}
                  <div className="p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-4 gap-3 sm:gap-4 items-start bg-white hover:bg-sky-50/20 transition-colors">
                    <div className="sm:col-span-1 pt-2">
                      <label className="font-medium text-gray-800 block">
                        Servidor de saída:
                      </label>
                    </div>
                    <div className="sm:col-span-3 space-y-3">
                      <div>
                        <input
                          type="text"
                          {...register('email_outgoing_host')}
                          placeholder="mail.cesire.com.br"
                          className={`w-full px-3.5 py-2.5 bg-slate-50/60 border rounded-xl text-slate-800 focus:bg-white focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all text-sm ${
                            errors.email_outgoing_host ? 'border-red-300 bg-red-50' : 'border-slate-200'
                          }`}
                        />
                        {errors.email_outgoing_host && (
                          <div className="flex items-center gap-1 mt-1 text-red-600 text-xs">
                            <AlertCircle className="h-3.5 w-3.5" />
                            <span>{String(errors.email_outgoing_host?.message || '')}</span>
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                        <div>
                          <label className="block text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1">
                            <span className="underline decoration-dotted font-bold text-slate-700">SMTP Port</span>: 465 (SSL)
                          </label>
                          <input
                            type="number"
                            {...register('email_smtp_port', { valueAsNumber: true })}
                            placeholder="465"
                            className="w-full px-3 py-2 bg-slate-50/60 border border-slate-200 rounded-lg text-xs text-slate-800 focus:bg-white focus:ring-2 focus:ring-sky-500"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Linha 5: Mensagem informativa do rodapé */}
                  <div className="p-4 bg-sky-50/60 flex items-center gap-2.5 text-sky-950 font-medium text-xs">
                    <Shield className="w-4 h-4 text-sky-600 shrink-0" />
                    <span>IMAP, POP3 e SMTP require authentication.</span>
                  </div>
                </div>
              </div>

              {/* Informações Complementares de Remetente e Segurança */}
              <div className="bg-slate-50/80 border border-slate-200/90 rounded-2xl p-5 space-y-4">
                <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-sky-600" />
                  Identificação do Remetente e Segurança Adicional
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                      Nome de Exibição do Remetente
                    </label>
                    <input
                      type="text"
                      {...register('email_from_name')}
                      placeholder="Better Now Eventos"
                      className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:ring-2 focus:ring-sky-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                      E-mail do Remetente (From)
                    </label>
                    <input
                      type="email"
                      {...register('email_from_address')}
                      placeholder="betternow@cesire.com.br"
                      className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:ring-2 focus:ring-sky-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                      Protocolo de Criptografia
                    </label>
                    <select
                      {...register('email_security')}
                      className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:ring-2 focus:ring-sky-500"
                    >
                      <option value="ssl_tls">SSL / TLS (Recomendado - Porta 465)</option>
                      <option value="starttls">STARTTLS (Porta 587)</option>
                      <option value="none">Nenhuma (Não seguro)</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <label className="flex items-center gap-3 p-3.5 bg-white border border-sky-200/60 rounded-xl cursor-pointer hover:bg-sky-50/30 transition-all">
                    <input
                      type="checkbox"
                      {...register('email_auth_required')}
                      className="w-4 h-4 text-sky-600 rounded border-gray-300 focus:ring-sky-500"
                    />
                    <div>
                      <span className="text-xs font-semibold text-slate-900 block">
                        Autenticação Obrigatória no Servidor (SMTP Auth)
                      </span>
                      <span className="text-[11px] text-slate-500 block">
                        Exige envio de usuário e senha na negociação SSL/TLS para garantir entrega das mensagens.
                      </span>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-3.5 bg-sky-50/70 border border-sky-300/80 rounded-xl cursor-pointer hover:bg-sky-50 transition-all">
                    <input
                      type="checkbox"
                      {...register('email_enabled')}
                      className="w-4 h-4 text-sky-600 rounded border-gray-300 focus:ring-sky-500"
                    />
                    <div>
                      <span className="text-xs font-bold text-sky-950 block flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5 text-sky-600" />
                        Envio Automático de E-mails Ativo
                      </span>
                      <span className="text-[11px] text-sky-800 block">
                        Dispara e-mails de confirmação e ingressos automaticamente aos compradores.
                      </span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Informativo de Modelos Personalizados por Evento */}
              <div className="bg-sky-50/70 border border-sky-200/90 rounded-2xl p-5 flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-sky-600 text-white flex items-center justify-center shrink-0 shadow-xs">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <div className="space-y-1">
                  <h4 className="text-sm font-bold text-sky-950">
                    Modelos de E-mail Personalizados por Evento
                  </h4>
                  <p className="text-xs text-sky-800 leading-relaxed">
                    Assim como no WhatsApp, os modelos e assuntos dos e-mails automáticos de confirmação, criação de pedido e cancelamento são <strong>configurados individualmente em cada evento</strong>.
                  </p>
                  <p className="text-xs text-sky-700 pt-1">
                    👉 Para personalizar ou testar os textos de um evento específico, acesse o menu <strong>Eventos &gt; Editar Evento &gt; aba &quot;Mensagens E-mail&quot;</strong>.
                  </p>
                </div>
              </div>

              {/* Ações Inferiores */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsEmailTestModalOpen(true)}
                  className="border border-sky-500 text-sky-700 bg-sky-50/40 hover:bg-sky-100/80 rounded-xl px-5 py-2.5 flex items-center gap-2 font-medium transition-all shadow-sm text-sm"
                >
                  <Play className="w-4 h-4 fill-sky-600/30" />
                  Testar Conexão
                </button>
                <button
                  type="button"
                  onClick={handleSubmit(onSubmit)}
                  disabled={saving}
                  className="bg-sky-600 hover:bg-sky-700 text-white rounded-xl px-6 py-2.5 flex items-center gap-2 font-semibold shadow-sm transition-all text-sm disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? 'Salvando...' : 'Salvar Alterações'}
                </button>
              </div>
            </div>
          )}

          {/* Categoria WhatsApp WAHA */}
          {activeCategory === 'waha' && (
            <div className="space-y-6">
              {/* Header do Card WAHA */}
              <div className="flex items-center gap-4 pb-4 border-b border-gray-100">
                <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 shadow-sm shrink-0">
                  <Globe className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Integração WhatsApp (WAHA)
                  </h3>
                  <p className="text-sm text-gray-500">
                    Configure os parâmetros do seu servidor próprio WAHA (WhatsApp HTTP API).
                  </p>
                </div>
              </div>

              {/* Indicador de Seção */}
              <div className="border-b border-gray-200">
                <div className="inline-block border-b-2 border-emerald-600 pb-3">
                  <span className="text-sm font-bold text-emerald-700">
                    Servidor WAHA
                  </span>
                </div>
              </div>

              {/* Formulário do Servidor WAHA */}
              <div className="space-y-6 pt-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* URL do Servidor */}
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <Globe className="w-3.5 h-3.5 text-slate-400" />
                      URL DO SERVIDOR WAHA
                    </label>
                    <input
                      type="text"
                      {...register('waha_api_url')}
                      placeholder="https://waha.tradersbots.com.br"
                      className={`w-full px-4 py-3.5 bg-slate-50/80 border rounded-2xl text-slate-800 focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all shadow-sm text-sm ${
                        errors.waha_api_url ? 'border-red-300 bg-red-50' : 'border-slate-200/90'
                      }`}
                    />
                    {errors.waha_api_url && (
                      <div className="flex items-center gap-1 mt-1 text-red-600 text-xs">
                        <AlertCircle className="h-3.5 w-3.5" />
                        <span>{String(errors.waha_api_url?.message || '')}</span>
                      </div>
                    )}
                    <p className="text-xs text-slate-400 mt-1.5">
                      Endereço base do container / servidor WAHA.
                    </p>
                  </div>

                  {/* Nome da Sessão */}
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <Smartphone className="w-3.5 h-3.5 text-slate-400" />
                      NOME DA SESSÃO (SESSION NAME)
                    </label>
                    <input
                      type="text"
                      {...register('waha_session_name')}
                      placeholder="Cesire"
                      className={`w-full px-4 py-3.5 bg-slate-50/80 border rounded-2xl text-slate-800 focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all shadow-sm text-sm ${
                        errors.waha_session_name ? 'border-red-300 bg-red-50' : 'border-slate-200/90'
                      }`}
                    />
                    {errors.waha_session_name && (
                      <div className="flex items-center gap-1 mt-1 text-red-600 text-xs">
                        <AlertCircle className="h-3.5 w-3.5" />
                        <span>{String(errors.waha_session_name?.message || '')}</span>
                      </div>
                    )}
                    <p className="text-xs text-slate-400 mt-1.5">
                      Identificador da sessão ativa no seu WAHA.
                    </p>
                  </div>
                </div>

                {/* Chave de API */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <Key className="w-3.5 h-3.5 text-slate-400" />
                      CHAVE DE API (WAHA_API_KEY)
                    </label>
                    <div className="relative">
                      <input
                        type={showApiKey ? 'text' : 'password'}
                        {...register('waha_api_key')}
                        placeholder="••••••••••••••••••••••••••••"
                        className={`w-full px-4 py-3.5 pr-11 bg-slate-50/80 border rounded-2xl text-slate-800 focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all shadow-sm text-sm font-mono ${
                          errors.waha_api_key ? 'border-red-300 bg-red-50' : 'border-slate-200/90'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowApiKey(!showApiKey)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1"
                      >
                        {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {errors.waha_api_key && (
                      <div className="flex items-center gap-1 mt-1 text-red-600 text-xs">
                        <AlertCircle className="h-3.5 w-3.5" />
                        <span>{String(errors.waha_api_key?.message || '')}</span>
                      </div>
                    )}
                    <p className="text-xs text-slate-400 mt-1.5">
                      Chave de segurança (cabeçalho X-Api-Key).
                    </p>
                  </div>

                  {/* Toggle Habilitado */}
                  <div className="flex flex-col justify-center">
                    <label className="flex items-center gap-3 p-4 bg-emerald-50/40 border border-emerald-200/60 rounded-2xl cursor-pointer hover:bg-emerald-50/80 transition-all">
                      <input
                        type="checkbox"
                        {...register('waha_enabled')}
                        className="w-5 h-5 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                      />
                      <div>
                        <span className="text-sm font-semibold text-gray-900 block">
                          Envio Automático Ativo
                        </span>
                        <span className="text-xs text-gray-500 block">
                          Dispara mensagens de confirmação e status automaticamente aos clientes.
                        </span>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Ações Inferiores idênticas ao print */}
                <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setIsTestModalOpen(true)}
                    className="border border-emerald-500 text-emerald-700 bg-emerald-50/40 hover:bg-emerald-100/80 rounded-xl px-5 py-2.5 flex items-center gap-2 font-medium transition-all shadow-sm text-sm"
                  >
                    <Play className="w-4 h-4 fill-emerald-600/30" />
                    Testar Conexão
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit(onSubmit)}
                    disabled={saving}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl px-6 py-2.5 flex items-center gap-2 font-semibold shadow-sm transition-all text-sm disabled:opacity-50"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {saving ? 'Salvando...' : 'Salvar Alterações'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeCategory === 'business_hours' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Horários de Funcionamento</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Horário de Funcionamento (Segunda a Sexta)
                </label>
                <input
                  type="text"
                  {...register('business_hours_weekdays')}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${errors.business_hours_weekdays
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-300'
                    }`}
                  placeholder="08:00 às 18:00"
                />
                {errors.business_hours_weekdays && (
                  <div className="flex items-center gap-1 mt-1 text-red-600 text-sm">
                    <AlertCircle className="h-4 w-4" />
                    <span>{String(errors.business_hours_weekdays?.message || '')}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Horário de Funcionamento (Sábado)
                </label>
                <input
                  type="text"
                  {...register('business_hours_weekend')}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${errors.business_hours_weekend
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-300'
                    }`}
                  placeholder="09:00 às 14:00"
                />
                {errors.business_hours_weekend && (
                  <div className="flex items-center gap-1 mt-1 text-red-600 text-sm">
                    <AlertCircle className="h-4 w-4" />
                    <span>{String(errors.business_hours_weekend?.message || '')}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dias Fechados
                </label>
                <input
                  type="text"
                  {...register('business_hours_closed_days')}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${errors.business_hours_closed_days
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-300'
                    }`}
                  placeholder="Domingos e feriados"
                />
                {errors.business_hours_closed_days && (
                  <div className="flex items-center gap-1 mt-1 text-red-600 text-sm">
                    <AlertCircle className="h-4 w-4" />
                    <span>{String(errors.business_hours_closed_days?.message || '')}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeCategory === 'carousel' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Configurações do Carrossel</h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      {...register('carousel_autoplay')}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Autoplay do Carrossel
                    </span>
                  </label>
                  {errors.carousel_autoplay && (
                    <div className="flex items-center gap-1 mt-1 text-red-600 text-sm">
                      <AlertCircle className="h-4 w-4" />
                      <span>{String(errors.carousel_autoplay?.message || '')}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Intervalo do Carrossel (s)
                  </label>
                  <input
                    type="number"
                    {...register('carousel_interval', { valueAsNumber: true })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${errors.carousel_interval
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                      }`}
                    min="1"
                    max="30"
                    step="1"
                  />
                  {errors.carousel_interval && (
                    <div className="flex items-center gap-1 mt-1 text-red-600 text-sm">
                      <AlertCircle className="h-4 w-4" />
                      <span>{String(errors.carousel_interval?.message || '')}</span>
                    </div>
                  )}
                  <p className="text-sm text-gray-500 mt-1">
                    Tempo entre transições das imagens em segundos
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Intervalo dos Depoimentos (s)
                  </label>
                  <input
                    type="number"
                    {...register('testimonial_carousel_interval', { valueAsNumber: true })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${errors.testimonial_carousel_interval
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                      }`}
                    min="1"
                    max="30"
                    step="1"
                  />
                  {errors.testimonial_carousel_interval && (
                    <div className="flex items-center gap-1 mt-1 text-red-600 text-sm">
                      <AlertCircle className="h-4 w-4" />
                      <span>{String(errors.testimonial_carousel_interval?.message || '')}</span>
                    </div>
                  )}
                  <p className="text-sm text-gray-500 mt-1">
                    Tempo entre transições dos depoimentos em segundos
                  </p>
                </div>
              </div>

              {/* Seção de Gerenciamento de Imagens do Carrossel */}
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-md font-semibold text-gray-900">Imagens do Carrossel</h4>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar Imagem
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={async (e) => {
                      const files = Array.from(e.target.files || []) as File[];
                      for (const file of files) {
                        await addImage(file, file.name.split('.')[0]);
                      }
                      e.target.value = '';
                    }}
                  />
                </div>

                {imagesError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <div className="flex items-center">
                      <AlertCircle className="h-4 w-4 text-red-400 mr-2" />
                      <span className="text-sm text-red-700">{imagesError}</span>
                    </div>
                  </div>
                )}

                {imagesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
                    <span className="ml-2 text-sm text-gray-600">Carregando imagens...</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {images.filter(img => !img.deleted).map((image) => (
                      <div key={image.id} className="relative group bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                        <div className="aspect-video bg-gray-100">
                          <img
                            src={image.file_url}
                            alt={image.title}
                            className={`w-full h-full object-cover transition-all duration-300 ${!image.active ? 'filter grayscale opacity-60' : ''
                              }`}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMiA5VjEzTTEyIDE3SDE2TTE2IDlIMTJNMTIgOUg4VjEzSDEyVjlaTTggMTNWMTdIMTJWMTNIOFoiIHN0cm9rZT0iIzlDQTNBRiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPC9zdmc+';
                            }}
                          />
                        </div>

                        <div className="p-3">
                          <h5 className="font-medium text-gray-900 text-sm truncate">{image.title}</h5>
                          <p className="text-xs text-gray-500 mt-1">
                            Ordem: {image.order_position} | {image.active ? 'Ativo' : 'Inativo'}
                          </p>
                        </div>

                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="flex space-x-1">
                            <button
                              type="button"
                              onClick={() => toggleImage(image.id)}
                              className={`p-1 rounded-full ${image.active
                                  ? 'bg-green-100 text-green-600 hover:bg-green-200'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                              title={image.active ? 'Desativar' : 'Ativar'}
                            >
                              {image.active ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                            </button>
                            <button
                              type="button"
                              onClick={() => moveToTrash(image.id)}
                              className="p-1 rounded-full bg-red-100 text-red-600 hover:bg-red-200"
                              title="Mover para lixeira"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {images.filter(img => !img.deleted).length === 0 && !imagesLoading && (
                  <div className="text-center py-8">
                    <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Nenhuma imagem do carrossel encontrada</p>
                    <p className="text-sm text-gray-400 mt-1">Clique em "Adicionar Imagem" para começar</p>
                  </div>
                )}

                {/* Lixeira */}
                {getDeletedImages().length > 0 && (
                  <div className="border-t pt-6 mt-6">
                    <h5 className="text-sm font-medium text-gray-900 mb-3">Lixeira</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {getDeletedImages().map((image) => (
                        <div key={image.id} className="relative group bg-gray-50 border border-gray-200 rounded-lg overflow-hidden opacity-60">
                          <div className="aspect-video bg-gray-100">
                            <img
                              src={image.url}
                              alt={image.title}
                              className="w-full h-full object-cover"
                            />
                          </div>

                          <div className="p-3">
                            <h5 className="font-medium text-gray-700 text-sm truncate">{image.title}</h5>
                            <p className="text-xs text-gray-500 mt-1">Na lixeira</p>
                          </div>

                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="flex space-x-1">
                              <button
                                type="button"
                                onClick={() => restoreImage(image.id)}
                                className="p-1 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200"
                                title="Restaurar"
                              >
                                <RotateCcw className="h-3 w-3" />
                              </button>
                              <button
                                type="button"
                                onClick={() => permanentlyDelete(image.id)}
                                className="p-1 rounded-full bg-red-100 text-red-600 hover:bg-red-200"
                                title="Excluir permanentemente"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeCategory === 'system' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Configurações do Sistema</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tamanho Máximo de Arquivo (MB)
                  </label>
                  <input
                    type="number"
                    {...register('max_file_size', { valueAsNumber: true })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${errors.max_file_size
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                      }`}
                    min="1"
                    max="100"
                  />
                  {errors.max_file_size && (
                    <div className="flex items-center gap-1 mt-1 text-red-600 text-sm">
                      <AlertCircle className="h-4 w-4" />
                      <span>{String(errors.max_file_size?.message || '')}</span>
                    </div>
                  )}
                  <p className="text-sm text-gray-500 mt-1">
                    Tamanho máximo em MB para upload de arquivos
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Retenção de Backup (dias)
                  </label>
                  <input
                    type="number"
                    {...register('backup_retention_days', { valueAsNumber: true })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${errors.backup_retention_days
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                      }`}
                    min="1"
                    max="365"
                  />
                  {errors.backup_retention_days && (
                    <div className="flex items-center gap-1 mt-1 text-red-600 text-sm">
                      <AlertCircle className="h-4 w-4" />
                      <span>{String(errors.backup_retention_days?.message || '')}</span>
                    </div>
                  )}
                  <p className="text-sm text-gray-500 mt-1">
                    Quantos dias manter os backups
                  </p>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email para Notificações
                  </label>
                  <input
                    type="email"
                    {...register('notification_email')}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${errors.notification_email
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                      }`}
                    placeholder="admin@exemplo.com"
                  />
                  {errors.notification_email && (
                    <div className="flex items-center gap-1 mt-1 text-red-600 text-sm">
                      <AlertCircle className="h-4 w-4" />
                      <span>{String(errors.notification_email?.message || '')}</span>
                    </div>
                  )}
                  <p className="text-sm text-gray-500 mt-1">
                    Email para receber notificações do sistema
                  </p>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipos de Arquivo Permitidos
                  </label>
                  <input
                    type="text"
                    {...register('allowed_file_types')}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${errors.allowed_file_types
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                      }`}
                    placeholder="jpg,png,pdf,doc"
                  />
                  {errors.allowed_file_types && (
                    <div className="flex items-center gap-1 mt-1 text-red-600 text-sm">
                      <AlertCircle className="h-4 w-4" />
                      <span>{String(errors.allowed_file_types?.message || '')}</span>
                    </div>
                  )}
                  <p className="text-sm text-gray-500 mt-1">
                    Selecione os tipos de arquivo permitidos para upload
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Status Information */}
      {isDirty && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-yellow-800">
            <Bell className="h-5 w-5" />
            <span className="font-medium">Alterações não salvas</span>
          </div>
          <p className="text-yellow-700 mt-1">
            Você tem alterações não salvas. Clique em "Salvar Alterações" para aplicá-las.
          </p>
        </div>
      )}

      {/* Modal de Teste WAHA */}
      <WahaTestModal
        isOpen={isTestModalOpen}
        onClose={() => setIsTestModalOpen(false)}
        apiUrl={watch('waha_api_url')}
        sessionName={watch('waha_session_name')}
        apiKey={watch('waha_api_key')}
      />

      {/* Modal de Teste do Servidor de E-mail */}
      <EmailServerTestModal
        isOpen={isEmailTestModalOpen}
        onClose={() => setIsEmailTestModalOpen(false)}
        smtpUser={watch('email_smtp_user')}
        smtpPassword={watch('email_smtp_password')}
        incomingHost={watch('email_incoming_host')}
        imapPort={watch('email_imap_port') || 993}
        pop3Port={watch('email_pop3_port') || 995}
        outgoingHost={watch('email_outgoing_host')}
        smtpPort={watch('email_smtp_port') || 465}
        fromName={watch('email_from_name')}
        fromAddress={watch('email_from_address')}
        security={watch('email_security')}
        authRequired={watch('email_auth_required')}
      />
    </div>
  );
};

export default AdminSettings;